import { store } from '../store'
import DynamicModuleRegistrationUtil from '../utils/dynamicModuleRegistration'
import { OrdersEnum } from '../enums/modules/routes'
import ShipbobAppSettings from '@/appSettings/appSetting'
import Router from '../router'

/** Modals should not be dynamically imported. Causes an intermittent issue where the orders page is hit first,
 * which resets the filters.
 */
import ExportOrders from '@/components/orders/ExportOrders'
import CopyOrders from '@/components/orders/CopyOrders'
import OrderConfirmation from '@/components/orders/OrderConfirmation'
import ConfirmMove from '@/components/orders/ConfirmMove'

// Vuex Modules to load
const OrdersStoreModule = () =>
  import(/* webpackChunkName: "Orders" */ '../store/modules/orders')
const OrderDetailsStoreModule = () =>
  import(/* webpackChunkName: "OrderDetails" */ '../store/modules/orderDetails')

const CreateOrdersStoreModule = () => import(/* webpackChunkName: "CreateOrder" */ '../store/modules/createOrder')

const ReturnOrderStoreModule = () => import(/* webpackChunkName: "OrderDetails" */ '../store/modules/returnOrder')

// Components to load
const OrdersPlaceholder = () =>
  import(/* webpackChunkName: "Orders" */ '@/components/orders/OrdersPlaceholder')

const Orders = () =>
  import(/* webpackChunkName: "Orders" */ '@/components/orders/Orders')

const ImportOrders = () =>
  import(/* webpackChunkName: "ImportOrders" */ '@/components/orders/ImportOrders')

const OrderDetailConfirmation = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/OrderDetailConfirmation')

const OrderDetailConfirmMove = () =>
  import(/* webpackChunkName: "ConfirmMove" */ '@/components/orderDetails/OrderDetailConfirmMove')

const OrderPricing = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/OrderPricing')

const OrderDetailsPlaceholder = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/OrderDetailsPlaceholder')

const OrderDetails = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/OrderDetails')

const AddProductToOrder = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/AddProductToOrder')

const CustomerInformationModal = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/CustomerInformation')

const SplitOrder = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/SplitOrder')

const EditShippingDetailsModal = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/EditShippingDetails')

const EditGiftMessageModal = () =>
  import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/EditGiftMessage')

const GenerateReturnLabel = () => import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/GenerateReturnLabel')

const SubscribeToOrder = () => import(/* webpackChunkName: "OrderDetails" */ '@/components/orderDetails/modals/SubscribeToOrder')

// ******************************************************
// *************** Route Guard Functions ****************
// ******************************************************

const dynamicModuleRegistrationReturnOrderArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'returnOrder',
    module: ReturnOrderStoreModule,
    store,
    modulePath: 'returnOrder'
  }
}

const dynamicModuleRegistrationOrdersArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'orders',
    module: OrdersStoreModule,
    store,
    modulePath: 'orders'
  }
}

const dynamicModuleRegistrationOrderDetailsArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'orderDetails',
    module: OrderDetailsStoreModule,
    store,
    modulePath: 'orderDetails'
  }
}

const dynamicModuleRegistrationCreateOrderArgs = () => {
  return {
    modulesNamespace: store._modulesNamespaceMap,
    moduleName: 'createOrder',
    module: CreateOrdersStoreModule,
    store,
    modulePath: 'createOrder'
  }
}

export const orderDetailsRouteResolver = async (to, _from = null, next = () => {}) => {
  const orderId = to.params.orderId
  const newStrat = next
  const oldStrat = () => window.location.replace(`${ShipbobAppSettings.photoPrint}/App/index/#/Order/${orderId}`)
  let strategy
  let orderTypeSupported
  const enableVueOrderDetails = store.getters.enableVueOrderDetails
  if (enableVueOrderDetails) {
    orderTypeSupported = await store.dispatch('orderDetails/determineOrderTypeSupport', orderId)
  }
  if (to.name === OrdersEnum.OrderPricing) strategy = newStrat
  else if (enableVueOrderDetails) {
    if (orderTypeSupported) strategy = newStrat
    else strategy = oldStrat
  } else {
    strategy = oldStrat
  }
  strategy && strategy()
}

export const isInOrdersRoute = (route) => route.path.toLowerCase().indexOf('/orders/') >= 0

export const serializeRoute = ({ viewName, pagination, searchTerm }) => {
  const searchTermQuery = searchTerm && searchTerm.length > 0 ? searchTerm : undefined
  // eslint-disable-next-line no-mixed-operators
  const pageNumber = pagination && pagination.pageNumber || 1
  return {
    name: OrdersEnum.OrdersWithView,
    params: { view: viewName },
    query: { searchTerm: searchTermQuery, pageNumber }
  }
}

// ******************************************************
// ****************** Route Definitions *****************
// ******************************************************

export default [
  {
    path: '/Orders',
    component: OrdersPlaceholder,
    beforeEnter (_to, _from, next) {
      DynamicModuleRegistrationUtil(dynamicModuleRegistrationOrdersArgs()).then(
        () => next()
      )
    },
    // domain used for breadcrumbs
    meta: { domain: OrdersEnum.Orders },
    children: [
      {
        path: 'ImportOrder',
        component: ImportOrders,
        name: OrdersEnum.ImportOrder
      },
      {
        path: 'ExportOrders',
        component: ExportOrders,
        name: OrdersEnum.ExportOrders
      },
      {
        path: 'CopyOrders',
        component: CopyOrders,
        name: OrdersEnum.CopyOrders
      },
      {
        path: 'Confirmation',
        component: OrderConfirmation,
        name: 'OrderConfirmation',
        props: true
      },
      {
        path: 'OrderDetailsConfirmation',
        component: OrderDetailConfirmation,
        name: 'OrderDetailConfirmation',
        props: true
      },
      {
        path: '/Orders/ConfirmMove',
        component: ConfirmMove,
        name: 'ConfirmMove',
        props: true
      },
      {
        path: 'OrderDetailsConfirmMove',
        component: OrderDetailConfirmMove,
        name: 'OrderDetailConfirmMove',
        props: true
      },
      {
        path: ':orderId',
        component: OrderDetailsPlaceholder,
        name: OrdersEnum.OrderDetailsPlaceholder,
        props: (route) => ({
          orderId: parseInt(route.params.orderId)
        }),
        beforeEnter (to, from, next) {
          DynamicModuleRegistrationUtil(dynamicModuleRegistrationOrderDetailsArgs()).then(
            async () => {
              await orderDetailsRouteResolver(to, from, next)
            }
          )
        },
        children: [
          {
            path: 'pricing',
            component: OrderPricing,
            name: OrdersEnum.OrderPricing,
            props: (route) => ({
              orderId: parseInt(route.params.orderId)
            }),
            meta: { showSideNav: true },
            beforeEnter (to, from, next) {
              // if a subaccount user of a powered by account tries to load pricing information
              // redirect them to the Order Details instead
              const isPricingMFEEnabledForOrderPricing = store.getters.isPricingMFEEnabledForOrderPricing
              const { isPoweredBySubAccountUser } = store.getters['user/getExternalIdInfo']
              if (isPricingMFEEnabledForOrderPricing) {
                Router.push(`/pricing/order/${to.params.orderId}`)
              } else if (isPoweredBySubAccountUser) {
                Router.push({ name: OrdersEnum.OrderDetails, params: { orderId: to.params.orderId } })
              } else {
                next()
              }
            }
          },
          {
            path: 'add-products',
            component: AddProductToOrder,
            name: OrdersEnum.AddProductToOrder,
            meta: { showSideNav: false },
            beforeEnter (to, from, next) {
              const shipmentId = store.getters['orderDetails/shipment'].id
              const isEditable = store.getters['orderDetails/isEditable']
              if (shipmentId && isEditable) {
                next()
              } else {
                Router.push({ name: OrdersEnum.OrderDetails, params: { orderId: to.params.orderId } })
              }
            }
          },
          {
            path: 'split-order',
            component: SplitOrder,
            name: OrdersEnum.SplitOrder,
            meta: { showSideNav: false }
          },
          {
            path: 'generate-return-label',
            component: GenerateReturnLabel,
            name: OrdersEnum.GenerateReturnLabel,
            meta: { showSideNav: false, offset: { y: 100 }, scrollToTop: true },
            children: [],
            beforeEnter (_to, _from, next) {
              DynamicModuleRegistrationUtil(dynamicModuleRegistrationReturnOrderArgs()).then(() => next())
            }
          },
          {
            path: '/',
            component: OrderDetails,
            name: OrdersEnum.OrderDetails,
            props: (route) => ({
              orderId: parseInt(route.params.orderId)
            }),
            meta: { showSideNav: true },
            children: [
              {
                path: 'Edit-Customer',
                component: CustomerInformationModal,
                name: OrdersEnum.EditCustomerInformation,
                meta: { showSideNav: true }
              },
              {
                path: 'Subscribe',
                component: SubscribeToOrder,
                name: OrdersEnum.SubscribeToOrder,
                meta: { showSideNav: true }
              },
              {
                path: 'Unsubscribe',
                component: SubscribeToOrder,
                name: OrdersEnum.UnsubscribeToOrder,
                meta: { showSideNav: true }
              },
              {
                path: 'Edit-GiftMessage',
                component: EditGiftMessageModal,
                name: OrdersEnum.EditGiftMessage,
                meta: { showSideNav: true }
              },
              {
                path: 'Edit-ShippingDetails',
                component: EditShippingDetailsModal,
                name: OrdersEnum.EditShippingDetails,
                meta: { showSideNav: true },
                beforeEnter (to, from, next) {
                  const shipmentId = store.getters['orderDetails/shipment'].id
                  if (shipmentId) {
                    next()
                  } else {
                    Router.push({ name: OrdersEnum.OrderDetails, params: { orderId: to.params.orderId } })
                  }
                }
              }
            ]
          }
        ]
      },
      {
        path: 'views/:view',
        component: Orders,
        name: OrdersEnum.OrdersWithView,
        meta: { showSideNav: true, domain: OrdersEnum.Orders },
        beforeEnter (_to, _from, next) {
          DynamicModuleRegistrationUtil(dynamicModuleRegistrationCreateOrderArgs()).then(() => next())
        }
      },
      {
        path: '/',
        component: Orders,
        name: OrdersEnum.Orders,
        beforeEnter (_to, _from, next) {
          const viewName = store.getters['orders/activeView'].name
          const searchTerm = store.getters['orders/searchTerm']
          const pagination = store.getters['orders/pagination']
          // This redirects legacy orders routes to the views route
          next(serializeRoute({ viewName, searchTerm, pagination }))
        },
        meta: { showSideNav: true, breadcrumbsIgnore: true }
      }
    ]
  },
  {
    path: '/Orders/Search/:search_term',
    component: Orders,
    name: OrdersEnum.OrdersWithSearch,
    beforeEnter (to, _from, next) {
      DynamicModuleRegistrationUtil(dynamicModuleRegistrationOrdersArgs()).then(
        async () => {
          const viewName = store.getters['orders/activeView'].name
          const searchTerm = to.params.search_term
          const pagination = store.getters['orders/pagination']
          // This redirects legacy orders routes with search terms to the views route with a search term
          next(serializeRoute({ viewName, searchTerm, pagination }))
        }
      )
    },
    meta: { showSideNav: true, domain: OrdersEnum.Orders, breadcrumbsIgnore: true }
  }
]
